import type { ReactNode } from 'react';
import { Alert, Sheet, Typography } from '@mui/joy';
import wordmark from './assets/consolidocs-wordmark.png';
import type { SignInBackdrop } from './useSignIn';
// The apps load upright Roboto only; the italic placeholders need its real
// italic face rather than a slanted fake.
import '@fontsource/roboto/400-italic.css';
import './SignInScreen.scss';

export interface SignInNotice {
  color: 'success' | 'warning' | 'danger';
  title: string;
  message: string;
}

export interface SignInCardProps {
  title: string;
  notice?: SignInNotice;
  backdrop?: SignInBackdrop;
  children: ReactNode;
}

/**
 * The shapes of Cognito's default managed-login background, laid out the
 * same way (the SVG covers the page like `background-size: cover`), but in
 * flat Joy colours so they follow the light/dark mode.
 */
const ShapesBackdrop = () => (
  <svg
    className="sign-in__shapes"
    viewBox="0 0 10000 4000"
    preserveAspectRatio="xMidYMid slice"
    aria-hidden="true"
    focusable="false"
  >
    <rect
      className="sign-in__shape"
      x="-1750"
      y="-1830"
      width="7740"
      height="5570"
      rx="80"
      transform="rotate(35 -1750 -1830)"
    />
    <rect
      className="sign-in__shape"
      x="6530"
      y="2670"
      width="5030"
      height="3450"
      rx="80"
    />
    <circle
      className="sign-in__shape sign-in__shape--round"
      cx="7540"
      cy="-720"
      r="2720"
    />
  </svg>
);

/**
 * The sign-in page's frame, styled after the Cognito managed login page
 * (same background, card and wordmark) so the two read as one product.
 */
export const SignInCard = ({
  title,
  notice,
  backdrop = 'photo',
  children,
}: SignInCardProps) => (
  <div className="sign-in">
    {backdrop === 'shapes' ? (
      <ShapesBackdrop />
    ) : (
      <div className="sign-in__backdrop" aria-hidden="true" />
    )}

    {/* White like the managed login card, rather than Joy's off-white surface. */}
    <Sheet
      variant="outlined"
      className="sign-in__card"
      sx={{ bgcolor: 'background.popup' }}
    >
      <img src={wordmark} alt="Consolidocs" className="sign-in__wordmark" />
      <Typography level="h3" component="h1">
        {title}
      </Typography>

      {notice && (
        <Alert variant="soft" color={notice.color} className="sign-in__notice">
          <div>
            <Typography level="title-sm" color={notice.color}>
              {notice.title}
            </Typography>
            <Typography level="body-sm">{notice.message}</Typography>
          </div>
        </Alert>
      )}

      {children}
    </Sheet>
  </div>
);
